import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import bonusService, { Bonus } from '../services/bonusService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Search, Edit, Trash2, DollarSign } from 'lucide-react';
import { useListFetch } from '../hooks/useListFetch';
import { ListFetchStates } from '../components/common/ListFetchStates';

interface BonusListProps {
  onCreateClick: () => void;
  onEditClick: (bonus: Bonus) => void;
  refreshTrigger?: number;
}

const ModernBonusList: React.FC<BonusListProps> = ({
  onCreateClick,
  onEditClick,
  refreshTrigger,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bonusToDelete, setBonusToDelete] = useState<Bonus | null>(null);

  const { data, isLoading, isError, fetchData } = useListFetch<{
    bonuses: Bonus[];
    total: number;
  }>();

  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });

  const loadBonuses = async () => {
    try {
      const result = await bonusService.getBonuses({
        page: pagination.page,
        perPage: pagination.perPage,
        q: searchTerm || undefined,
      });

      const bonusesArray = Array.isArray(result) ? result : [];
      const total = (result as any).__total || bonusesArray.length;

      setBonuses(bonusesArray);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      console.error('Error loading bonuses:', error);
      notify(
        translate('pos.bonuses.load_error', 'Failed to load bonuses'),
        { type: 'error' }
      );
    }
  };

  useEffect(() => {
    loadBonuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.perPage, searchTerm, refreshTrigger]);

  const handleDeleteClick = (bonus: Bonus) => {
    setBonusToDelete(bonus);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (bonusToDelete) {
      try {
        await bonusService.deleteBonus(bonusToDelete.id);
        notify(
          translate('pos.bonuses.delete_success', 'Bonus deleted successfully'),
          { type: 'success' }
        );
        setDeleteDialogOpen(false);
        setBonusToDelete(null);
        loadBonuses();
      } catch (error) {
        console.error('Error deleting bonus:', error);
        notify(
          translate('pos.bonuses.delete_error', 'Failed to delete bonus'),
          { type: 'error' }
        );
      }
    }
  };

  const formatConditions = (bonus: Bonus): string => {
    const conditions: string[] = [];
    if (bonus.position) {
      // Translate position value
      const positionTranslations: Record<string, string> = {
        'Barista': translate('pos.bonuses.position.barista', 'Barista'),
        'Cashier': translate('pos.bonuses.position.cashier', 'Cashier'),
        'Manager': translate('pos.bonuses.position.manager', 'Manager'),
        'Cook': translate('pos.bonuses.position.cook', 'Cook'),
        'Server': translate('pos.bonuses.position.server', 'Server'),
      };
      conditions.push(positionTranslations[bonus.position] || bonus.position);
    }
    if (bonus.staffingType) {
      const staffingTypeLabels: Record<string, string> = {
        'full-time': translate('pos.staff.profile.full_time', 'Full-Time'),
        'part-time': translate('pos.staff.profile.part_time', 'Part-Time'),
        'contract': translate('pos.staff.profile.contract', 'Contract'),
      };
      conditions.push(staffingTypeLabels[bonus.staffingType] || bonus.staffingType);
    }
    if (bonus.timeOfDay) {
      if (bonus.timeOfDay === 'custom' && bonus.customTimeRange) {
        conditions.push(
          `${bonus.customTimeRange.startTime} - ${bonus.customTimeRange.endTime}`
        );
      } else {
        conditions.push(
          translate(`pos.bonuses.time_of_day.${bonus.timeOfDay}`, bonus.timeOfDay)
        );
      }
    }
    if (bonus.dayOfWeek && bonus.dayOfWeek.length > 0) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const days = bonus.dayOfWeek
        .map((d) => translate(`pos.bonuses.day.${dayNames[d].toLowerCase()}`, dayNames[d]))
        .join(', ');
      conditions.push(days);
    }
    return conditions.length > 0
      ? conditions.join(' • ')
      : translate('pos.bonuses.all_conditions', 'All Conditions');
  };

  const filteredBonuses = useMemo(() => {
    if (!searchTerm) return bonuses;
    const lowerSearch = searchTerm.toLowerCase();
    return bonuses.filter(
      (bonus) =>
        bonus.name.toLowerCase().includes(lowerSearch) ||
        bonus.description?.toLowerCase().includes(lowerSearch) ||
        bonus.entityType.toLowerCase().includes(lowerSearch)
    );
  }, [bonuses, searchTerm]);

  if (isLoading && bonuses.length === 0) {
    return <ListFetchStates isLoading={isLoading} isError={isError} />;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                {translate('pos.bonuses.title', 'Bonuses')}
              </CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={translate('pos.bonuses.search', 'Search bonuses...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-64"
                />
              </div>
              <Button onClick={onCreateClick}>
                <Plus className="h-4 w-4 mr-2" />
                {translate('pos.bonuses.create', 'Create Bonus')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredBonuses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {translate('pos.bonuses.no_bonuses', 'No bonuses found.')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{translate('pos.bonuses.name', 'Name')}</TableHead>
                  <TableHead>{translate('pos.bonuses.entity_type', 'Entity Type')}</TableHead>
                  <TableHead>{translate('pos.bonuses.conditions', 'Conditions')}</TableHead>
                  <TableHead>{translate('pos.bonuses.amount', 'Amount')}</TableHead>
                  <TableHead>{translate('pos.bonuses.status', 'Status')}</TableHead>
                  <TableHead className="text-right">
                    {translate('pos.bonuses.actions', 'Actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBonuses.map((bonus) => (
                  <TableRow key={bonus.id}>
                    <TableCell className="font-medium">{bonus.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {translate(`pos.bonuses.entity_type.${bonus.entityType}`, bonus.entityType)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {formatConditions(bonus)}
                      </div>
                      {bonus.description && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {bonus.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">
                          {bonus.bonusAmount.toLocaleString()}
                        </span>
                        <Badge variant="outline">{bonus.bonusCurrency}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={bonus.isActive ? 'default' : 'secondary'}>
                        {bonus.isActive
                          ? translate('pos.bonuses.active', 'Active')
                          : translate('pos.bonuses.inactive', 'Inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditClick(bonus)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(bonus)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate('pos.bonuses.delete_title', 'Delete Bonus')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate(
                'pos.bonuses.delete_confirm',
                'Are you sure you want to delete this bonus? This action cannot be undone.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteDialogOpen(false);
                setBonusToDelete(null);
              }}
            >
              {translate('pos.bonuses.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {translate('pos.bonuses.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModernBonusList;

